// src/components/DailyPlanManagerModal.js
import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, 
    CircularProgress, Alert, Paper, IconButton, Tooltip, Switch,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    ListSubheader, Divider, Autocomplete, TextField,
    Typography, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import authFetch from '../utils/authFetch';
import { useAuth } from '../contexts/AuthContext';
import ActivityConfigModal from './ActivityConfigModal';

// UI Modules remain client-side only features for now
const UI_MODULES = [
    { key: 'ui_health_hub', label: 'UI: My Health Hub' },
    { key: 'ui_health_activities', label: 'UI: My Health Activities' },
    { key: 'ui_recovery_journal', label: 'UI: My Recovery Journal (Aria)' },
    { key: 'ui_gamification', label: 'UI: Stay Sharp (Gamification)' },
    { key: 'ui_mini_calendar', label: 'UI: Weekly Progress' },
];

const generateInstanceId = () => `trk_inst_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

export default function DailyPlanManagerModal({ 
    open, onClose, patientUserId, patientName, showSnackbar, 
    episodeId, lifetimeEpisodeId, activeEpisodes = [] 
}) {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    
    const [targetContextId, setTargetContextId] = useState(
        activeEpisodes.length > 0 ? activeEpisodes[0].id : (episodeId || lifetimeEpisodeId)
    );

    useEffect(() => {
        if (activeEpisodes.length > 0 && !targetContextId) {
            setTargetContextId(activeEpisodes[0].id);
        } else if (!targetContextId) {
            setTargetContextId(lifetimeEpisodeId);
        }
    }, [activeEpisodes, lifetimeEpisodeId, targetContextId]);

    const [editingDirective, setEditingDirective] = useState(null); 
    const [activityConfig, setActivityConfig] = useState(null);
    const [activityName, setActivityName] = useState(""); 
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [selectedLibraryItem, setSelectedLibraryItem] = useState(null);

    // --- Queries ---
    const directivesQueryKey = useMemo(() => ['patientAllDirectives', { patientUserId }], [patientUserId]);
    
    const { data: allDirectives = [], isLoading: isLoadingDirectives, isError } = useQuery({
        queryKey: directivesQueryKey,
        queryFn: () => authFetch(`/patients/${patientUserId}/directives`),
        enabled: !!open && !!patientUserId,
    });

    // UPDATED: Fetch ALL content to dynamically build the Standard list
    const { data: allContentItems = [], isLoading: isLoadingLibrary } = useQuery({
        queryKey: ['allContentItems_modal'],
        queryFn: () => authFetch('/api/content/?status=active&latest_only=true&limit=500'),
        enabled: !!open
    });

    const { systemTemplates, customTrackersLibrary } = useMemo(() => {
        const system = [];
        const custom = [];
        allContentItems.forEach(item => {
            // Use the new 'key' field or metadata flag to identify system items
            if (item.key || item.item_metadata?.is_system_template) {
                system.push(item);
            } else if (item.content_type === 'activity_log_request') {
                custom.push(item);
            }
        });
        return { systemTemplates: system, customTrackersLibrary: custom };
    }, [allContentItems]);

    const mutationConfig = (msg) => ({
        onSuccess: () => {
            if (msg) showSnackbar(msg, 'success');
            queryClient.invalidateQueries({ queryKey: directivesQueryKey });
            queryClient.invalidateQueries({ queryKey: ['patientDashboardData'] }); 
            setIsConfigModalOpen(false);
        },
        onError: (err) => showSnackbar(err.detail || 'Error updating plan.', 'error'),
    });

    const updateMutation = useMutation({
        mutationFn: (vars) => authFetch(`directives/${vars.directiveId}`, { method: 'PATCH', body: vars.payload }),
        ...mutationConfig('Plan updated.')
    });

    const createMutation = useMutation({
        mutationFn: (payload) => authFetch(`directives/`, { method: 'POST', body: payload }),
        ...mutationConfig('Item assigned.')
    });

    const { standardPlan, uiPlan, customTrackers } = useMemo(() => {
        const directiveMap = new Map(); // Maps content_item_id OR item_type to directive
        const trackers = [];
        const sortedDirectives = [...allDirectives].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        const targetEpisode = activeEpisodes.find(e => e.id === targetContextId);
        const targetMandates = targetEpisode?.mandated_daily_checkins || [];

        sortedDirectives.forEach(d => {
            const associatedEpisodeIds = d.associated_episodes ? d.associated_episodes.map(e => e.id) : [];
            const isRelevantToContext = targetContextId && associatedEpisodeIds.includes(targetContextId);
            
            // Check both Content Item ID (New way) and Item Type (Legacy way)
            const contentItemId = d.details?.item_metadata?.content_item_id;
            if (contentItemId) directiveMap.set(contentItemId, d);
            if (d.item_type) directiveMap.set(d.item_type, d);
            // [FIX] Accept both legacy 'activity' and new 'activity_log_request' types
            if ((d.item_type === 'activity' || d.item_type === 'activity_log_request') && d.status === 'active') {
                if (isRelevantToContext || associatedEpisodeIds.length === 0) {
                    trackers.push(d);
                }
            }
        });

        // Dynamically map System Templates from API
        const mappedStandardPlan = systemTemplates.map(template => {
            // Try finding directive by UUID first, then by Key/Type
            const directive = directiveMap.get(template.id) || directiveMap.get(template.key || template.content_type);
            
            let isActive = false;
            if (targetEpisode && targetEpisode.program?.key !== 'lifetime_perpetual') {
                // Check if mandated in this episode (using stable key)
                // The new backend saves content config, so we check if the directive exists & is linked
                if (directive && directive.status === 'active') {
                    const links = directive.associated_episodes?.map(e=>e.id) || [];
                    if (links.includes(targetContextId)) isActive = true;
                }
                // Fallback: check old string mandates
                if (template.key && targetMandates.includes(template.key)) isActive = true;
            } else {
                isActive = directive?.status === 'active';
            }

            return {
                id: template.id,
                key: template.key, // e.g. 'vitals'
                label: template.title,
                contentType: template.content_type,
                directive: directive,
                isActive: isActive
            };
        });

        const mapUiPlan = (list) => list.map(type => {
            const directive = directiveMap.get(type.key);
            let isActive = directive?.status === 'active';
            if (targetEpisode && targetEpisode.program?.key !== 'lifetime_perpetual') {
                 // UI flags currently default to Lifetime, but check if specifically linked
                 const links = directive?.associated_episodes?.map(e=>e.id) || [];
                 if (links.includes(targetContextId)) isActive = true;
                 // Fallback to global if not specific
                 else if (directive?.status === 'active' && links.length === 1 && links[0] === lifetimeEpisodeId) isActive = true;
            }
            return { ...type, directive: directive, isActive: isActive };
        });

        return { standardPlan: mappedStandardPlan, uiPlan: mapUiPlan(UI_MODULES), customTrackers: trackers };
    }, [allDirectives, targetContextId, activeEpisodes, systemTemplates]);

    const handleToggleSingleton = (planItem) => {
        // Optimistically update the cache BEFORE calling the mutation
        const previousDirectives = queryClient.getQueryData(directivesQueryKey);
        
        const newStatus = planItem.isActive ? 'stopped' : 'active';
        const isCreating = !planItem.isActive;

        // 1. Optimistic Update Function
        const optimisticUpdate = (isCreating) => {
            queryClient.setQueryData(directivesQueryKey, (old) => {
                if (!old) return [];

                if (isCreating) {
                    // Simulate creation of the new active directive
                    const newDirective = {
                        id: generateInstanceId(), // Temporary ID
                        patient_user_id: patientUserId,
                        tenant_id: user.master_tenant_id, // Assuming this is needed for the directive structure
                        item_type: planItem.key || planItem.contentType,
                        status: newStatus,
                        created_at: new Date().toISOString(),
                        associated_episodes: [{ id: lifetimeEpisodeId || episodeId }],
                        details: {
                            item_name: planItem.label,
                            item_metadata: { content_item_id: planItem.id },
                            // Add other minimal details to satisfy the map function
                            frequency_type: (planItem.key || '').startsWith('ui_') ? 'once' : 'daily',
                            times_of_day: ["09:00"],
                            regimen_start_date: new Date().toISOString().split('T')[0],
                            reminders_active: !(planItem.key || '').startsWith('ui_'),
                        }
                    };
                    return [...old, newDirective];

                } else if (planItem.directive) {
                    // Simulate stopping the existing directive
                    return old.map(d =>
                        d.id === planItem.directive.id ? { ...d, status: newStatus } : d
                    );
                }
                return old;
            });
        };

        if (planItem.isActive) {
            // Stopping an existing directive
            optimisticUpdate(false);
            updateMutation.mutate({ directiveId: planItem.directive.id, payload: { status: 'stopped' } });
        } else {
            // Creating a new directive
            const targetId = lifetimeEpisodeId || episodeId;
            const payload = {
                patient_user_id: patientUserId,
                episode_of_care_id: targetId, // Pass Lifetime ID by default for standard items
                regimen_items: [{
                    item_name: planItem.label,
                    item_type: planItem.key || planItem.contentType,
                    frequency_type: (planItem.key || '').startsWith('ui_') ? 'once' : 'daily',
                    times_of_day: ["09:00"],
                    regimen_start_date: new Date().toISOString().split('T')[0],
                    reminders_active: !(planItem.key || '').startsWith('ui_'),
                    item_metadata: {
                        content_item_id: planItem.id
                    }
                }]
            };
            optimisticUpdate(true); // Optimistic update for creation
            createMutation.mutate(payload);
        }

        // Add rollback logic in mutationConfig onError
        mutationConfig.onError = (err, variables) => {
             showSnackbar(err.detail || 'Error updating plan.', 'error');
             // Rollback: Revert to the original data if the mutation fails
             queryClient.setQueryData(directivesQueryKey, previousDirectives);
             setIsConfigModalOpen(false);
        };
    };
    
    const handleStopTracker = (directiveId) => {
        if(window.confirm("Stop this tracker? This will hide it from the patient.")) {
            updateMutation.mutate({ directiveId: directiveId, payload: { status: 'stopped' } });
        }
    };

    const handleLibrarySelect = async (event, newItem) => {
        if (!newItem) return;
        try {
            const fullItem = await authFetch(`/api/content/${newItem.id}`);
            setEditingDirective(null); 
            setActivityName(fullItem.title || fullItem.item_name);
            let loadedConfig = fullItem.item_metadata || {};
            if (loadedConfig.tracker_config) loadedConfig = { ...loadedConfig.tracker_config };
            loadedConfig = { ...loadedConfig, tracker_namespace: generateInstanceId() };
            setActivityConfig(loadedConfig); 
            setSelectedLibraryItem(null);
            setIsConfigModalOpen(true);
        } catch (error) {
            showSnackbar("Failed to load tracker configuration.", "error");
        }
    };

    const handleCreateBlank = () => {
        setEditingDirective(null);
        setActivityName("");
        setActivityConfig({ tracker_namespace: generateInstanceId() }); 
        setIsConfigModalOpen(true);
    };

    const handleEditActive = (tracker) => {
        setEditingDirective(tracker);
        setActivityName(tracker.details?.item_name || "");
        const existingConfig = tracker.details?.item_metadata || {};
        if (!existingConfig.tracker_namespace) existingConfig.tracker_namespace = generateInstanceId();
        setActivityConfig(existingConfig);
        setIsConfigModalOpen(true);
    };

    const handleSaveTrackerConfig = ({ name, config }) => {
        if (!name) return showSnackbar("Name is required", "error");
        if (editingDirective) {
             updateMutation.mutate({
                 directiveId: editingDirective.id,
                 payload: { details: { ...editingDirective.details, item_name: name, item_metadata: config } }
             });
        } else {
            if (!targetContextId) return showSnackbar("Please select a Program Context.", "error");
            if (targetContextId === lifetimeEpisodeId) {
                if(!window.confirm("Assigning to 'General/Lifetime'. Continue?")) return;
            }
            createMutation.mutate({
                patient_user_id: patientUserId,
                episode_of_care_id: targetContextId,
                regimen_items: [{
                    item_name: name, 
                    item_type: 'activity',
                    frequency_type: 'daily',
                    times_of_day: ["09:00"],
                    regimen_start_date: new Date().toISOString().split('T')[0],
                    reminders_active: true,
                    item_metadata: config
                }]
            });
        }
    };

    const isLoading = isLoadingDirectives || isLoadingLibrary;

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Daily Plan: {patientName}
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #eee' }}>
                        <TextField
                            select fullWidth size="small"
                            label="Target Program for New Trackers"
                            value={targetContextId || ''}
                            onChange={(e) => setTargetContextId(e.target.value)}
                            helperText="Select which program this new tracker belongs to."
                            InputLabelProps={{ shrink: true }}
                        >
                            {activeEpisodes.map(ep => (
                                <MenuItem key={ep.id} value={ep.id}>{ep.program?.name || 'Care Plan'} ({ep.status})</MenuItem>
                            ))}
                            {activeEpisodes.length > 0 && <Divider />}
                            <MenuItem value={lifetimeEpisodeId}>General / Lifetime (Visible Everywhere)</MenuItem>
                        </TextField>
                    </Box>

                    {isLoading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
                    {isError && <Alert severity="error" sx={{ m: 2 }}>Error loading plan</Alert>}
                    
                    {!isLoading && !isError && (
                        <Paper elevation={0} sx={{ m: 0 }}>
                            <List disablePadding>
                                <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Assign Activity Tracker</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Autocomplete
                                            fullWidth size="small"
                                            options={customTrackersLibrary}
                                            value={selectedLibraryItem}
                                            getOptionLabel={(option) => option.title || option.item_name || 'Unknown'}
                                            onChange={handleLibrarySelect}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder="Search Library (e.g. 'Knee Brace')" InputProps={{ ...params.InputProps, startAdornment: (<><LibraryBooksIcon color="action" fontSize="small" sx={{ mr: 1, ml: 0.5 }} />{params.InputProps.startAdornment}</>)}}/>
                                            )}
                                        />
                                        <Button variant="outlined" sx={{ minWidth: 'auto', px: 2 }} onClick={handleCreateBlank} startIcon={<PlaylistAddIcon />}>New</Button>
                                    </Box>
                                </Box>

                                <ListSubheader sx={{ bgcolor: '#f5f5f5', lineHeight: '40px' }}>Active Trackers ({customTrackers.length})</ListSubheader>
                                {customTrackers.length === 0 ? (
                                    <ListItem><ListItemText secondary="No custom trackers assigned." secondaryTypographyProps={{ fontStyle: 'italic', fontSize: '0.85rem' }} /></ListItem>
                                ) : (
                                    customTrackers.map((tracker) => (
                                        <ListItem key={tracker.id} divider sx={{ bgcolor: 'white' }}>
                                            <ListItemText primary={tracker.details?.item_name || "Unnamed Tracker"} primaryTypographyProps={{ fontWeight: 500 }} />
                                            <ListItemSecondaryAction>
                                                <Tooltip title="Configure"><IconButton size="small" onClick={() => handleEditActive(tracker)} sx={{ mr: 0.5 }}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="Stop/Remove"><IconButton size="small" onClick={() => handleStopTracker(tracker.id)} color="default"><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                )}

                                <ListSubheader sx={{ bgcolor: '#f5f5f5', lineHeight: '40px' }}>Standard Modules</ListSubheader>
                                {standardPlan.map((item) => (
                                    <ListItem key={item.id} divider sx={{ bgcolor: 'white' }}>
                                        <ListItemText primary={item.label} secondary={item.key ? `System Key: ${item.key}` : ''} secondaryTypographyProps={{fontSize: '0.7rem'}} />
                                        <ListItemSecondaryAction>
                                            <Switch checked={!!item.isActive} onChange={() => handleToggleSingleton(item)} size="small" color="primary" />
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                                
                                <ListSubheader sx={{ bgcolor: '#f5f5f5', lineHeight: '40px' }}>App Features</ListSubheader>
                                {uiPlan.map((item) => (
                                    <ListItem key={item.key} divider sx={{ bgcolor: 'white' }}>
                                        <ListItemText primary={item.label} />
                                        <ListItemSecondaryAction>
                                            <Switch checked={!!item.isActive} onChange={() => handleToggleSingleton(item)} size="small" color="default" />
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 1.5, bgcolor: '#f5f5f5', borderTop: '1px solid #ddd' }}>
                    <Button onClick={onClose} color="inherit">Done</Button>
                </DialogActions>
            </Dialog>
            <ActivityConfigModal open={isConfigModalOpen} onClose={() => setIsConfigModalOpen(false)} initialName={activityName} config={activityConfig} onSave={handleSaveTrackerConfig} />
        </>
    );
}